import { useState } from 'react';
import PosRoleModal from './PosRoleModal';
import { spinnerRing } from '../utils/bookingUtils';
import { POS_PERMISSIONS } from '../constants/posConst';
import { isStoreAdminRole } from '../utils/roleUtil';
import '../css/PosAdmin.css';

/**
 * The labels for a set of permission keys, in the order they are listed in the
 * dialog — so a role card reads the same way twice running.
 *
 * @param {string[]} keys
 * @returns {string[]}
 */
const labelsFor = (keys) => POS_PERMISSIONS
  .filter(({ key }) => keys.includes(key))
  .map(({ label }) => label);

/**
 * One client's roles: the list, and the dialog that edits it.
 *
 * A controlled component — it owns no roles of its own, only which dialog is
 * open. The form above it holds the list and writes it on save, which is how
 * the Staff tab works and why a role can be defined before the client it
 * belongs to has been registered.
 *
 * @param {object} props
 * @param {Array<{id: string, name: string, permissions: string[]}>} props.roles
 * @param {(roles: Array<object>) => void} props.onChange
 * @param {Object<string, number>} [props.assignedCounts] - how many staff
 *   accounts hold each role id, so a role in use cannot be removed out from
 *   under them.
 * @param {boolean} [props.loading] - the existing roles are still being read.
 * @param {?Error} [props.loadError] - the read failed; see the form's note on
 *   why the list must then be left alone rather than saved back empty.
 * @param {boolean} [props.disabled]
 * @param {Set<string>} [props.customisedIds] - roles whose rights the club's
 *   store admin has changed. Shown so the administrator knows the defaults
 *   here are not what that club's staff actually get.
 * @returns {JSX.Element}
 */
function PosClientRoles({
  roles,
  onChange,
  assignedCounts = {},
  loading = false,
  loadError = null,
  disabled = false,
  customisedIds = new Set(),
}) {
  // null = closed. { role: null } = adding; { role } = editing that one.
  const [editing, setEditing] = useState(null);

  /**
   * Replaces the edited role, or appends a new one.
   *
   * Matched by id rather than by position, because validateRole derives the id
   * from the name and an edit that renames a role must still land on the same
   * entry.
   *
   * @param {{id: string, name: string, permissions: string[]}} role
   * @returns {void}
   */
  const handleSave = (role) => {
    const exists = roles.some((other) => other.id === role.id);
    onChange(exists
      ? roles.map((other) => (other.id === role.id ? role : other))
      : [...roles, role]);
    setEditing(null);
  };

  /**
   * Removes a role from the list. Takes effect when the form is saved.
   *
   * @param {string} id
   * @returns {void}
   */
  const handleRemove = (id) => onChange(roles.filter((role) => role.id !== id));

  if (loading) {
    return <div className="pa-loading pa-loading-inline">{spinnerRing('Loading roles')}</div>;
  }

  if (loadError) {
    return (
      <>
        <p className="pa-field-hint">
          This client&rsquo;s roles could not be read: {loadError.message}
        </p>
        <p className="pa-field-hint">
          They are being left exactly as they are — saving this form will change the
          client&rsquo;s details only. Usually this means the database rules have not been
          deployed, or this account is not one the rules name as an administrator.
        </p>
      </>
    );
  }

  return (
    <>
      <div className="pa-role-list">
        {roles.length === 0 ? (
          <p className="pa-field-hint">No roles yet.</p>
        ) : roles.map((role) => {
          const granted = labelsFor(role.permissions);
          // Removing a role people hold is refused rather than cascaded.
          // Clearing their assignment would hand every holder FULL access — an
          // unassigned account is unrestricted — which is the opposite of what
          // deleting a role is meant to do.
          const held = assignedCounts[role.id] || 0;
          return (
            <div className="pa-role-card" key={role.id}>
              <div className="pa-role-card-head">
                <span className="pa-role-name">
                  {role.name}
                  {held > 0 && (
                    <span className="pa-role-held">
                      {' '}&middot; held by {held} account{held === 1 ? '' : 's'}
                    </span>
                  )}
                  {isStoreAdminRole(role) && <span className="ps-role-badge ps-role-badge-admin">Store admin</span>}
                  {!isStoreAdminRole(role) && customisedIds.has(role.id) && (
                    <span className="ps-role-badge" title="The club's store admin has changed what this role may do. Their version applies at the till.">
                      Customised by club
                    </span>
                  )}
                </span>
                <div className="pa-role-card-actions">
                  <button
                    type="button"
                    className="pa-btn pa-btn-quiet"
                    onClick={() => setEditing({ role })}
                    disabled={disabled}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="pa-btn pa-btn-quiet"
                    onClick={() => handleRemove(role.id)}
                    disabled={disabled || held > 0}
                    title={held > 0 ? 'Reassign its accounts under Staff first.' : undefined}
                  >
                    Remove
                  </button>
                </div>
              </div>
              {isStoreAdminRole(role) ? (
                <p className="pa-role-perm-none">
                  Full access, can filter sales by staff, and can adjust the rights of this club&rsquo;s other roles.
                </p>
              ) : granted.length === 0 ? (
                <p className="pa-role-perm-none">Grants nothing.</p>
              ) : (
                <ul className="pa-role-perm-list">
                  {granted.map((label) => <li key={label}>{label}</li>)}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      <button
        type="button"
        className="pa-btn pa-btn-primary pa-role-add"
        onClick={() => setEditing({ role: null })}
        disabled={disabled}
      >
        Add Role
      </button>

      <PosRoleModal
        isOpen={Boolean(editing)}
        role={editing?.role || null}
        existingRoles={roles}
        onSave={handleSave}
        onClose={() => setEditing(null)}
      />
    </>
  );
}

export default PosClientRoles;
